import { useReactiveVar } from "@apollo/client";
import { Input } from "@dmm-com/rocket-web-ui-library";
import type { ChangeEvent } from "react";
import { useState } from "react";
import { AgreementContent } from "./agreement-content";
import * as Styled from "./style";
import { validateEmail } from "./utils/validate-email";
import { validateName } from "./utils/validate-name";
import { productIdVar } from "~/components/stores/detail-product/store";
import { runningInBrowser } from "~/const/browser";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import { useQueryString } from "~/hooks/use-query-string";

import { usePostPurchaseWakuwariVideoByCashierMutation } from "~/lib/graphql";
import { useAuth } from "~/provider/auth";
import { getDevice } from "~/util/get-device";

const defaultFormState = { value: "", isError: false, errorText: "" };

/** イベントワクワク割対象商品の場合に表示するコンポーネント */
export const EventWakuwakuContent = () => {
  // MEMO: network-only の場合、useMutation の loading が cache を見ないため
  const [isLoadingPurchaseWakuwakuVideoByCashier, setIsLoadingPurchaseWakuwakuVideoByCashier] = useState(false);

  // MEMO（kimura）: キャッシュされてしまうと、最悪期限が切れてしまうため、fetchPolicyを`network-only`に変更
  const [purchaseWakuwakuVideoByCashierExec] = usePostPurchaseWakuwariVideoByCashierMutation({
    fetchPolicy: "network-only",
  });

  const productId = useReactiveVar(productIdVar);
  const { handleClickByDevice } = useHandleClickByDevice();
  const { isLoggedIn, execAuth } = useAuth();
  const { purchaseContent } = useQueryString();

  const [familyName, setFamilyName] = useState(defaultFormState);
  const [givenName, setGivenName] = useState(defaultFormState);
  const [email, setEmail] = useState(defaultFormState);

  const postPurchaseWakuwakuPfCashier = async () => {
    if (isLoadingPurchaseWakuwakuVideoByCashier) return;
    setIsLoadingPurchaseWakuwakuVideoByCashier(true);

    if (!isLoggedIn) {
      execAuth();
      return;
    }

    if (!runningInBrowser) return;

    const currentUrl = new URL(location.href);
    const currentParams = currentUrl.searchParams;
    currentParams.delete("purchaseContent");

    const { data } = await purchaseWakuwakuVideoByCashierExec({
      variables: {
        input: {
          device: getDevice(),
          contentId: purchaseContent ?? "",
          productId,
          backUrl: `${currentUrl}&isPurchase=true`,
          givenName: givenName.value,
          familyName: familyName.value,
          mail: email.value,
        },
      },
    });

    if (!data) {
      setIsLoadingPurchaseWakuwakuVideoByCashier(false);
      return;
    }

    if (data.purchaseWakuwariVideoByCashier.mutationId) {
      location.href = data.purchaseWakuwariVideoByCashier.cashierUrl;
    } else {
      setIsLoadingPurchaseWakuwakuVideoByCashier(false);
    }
  };

  const handleFamilyNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFamilyName({
      ...defaultFormState,
      value: event.currentTarget.value,
      ...validateName(event.currentTarget.value),
    });
  };

  const handleGivenNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setGivenName({
      ...defaultFormState,
      value: event.currentTarget.value,
      ...validateName(event.currentTarget.value),
    });
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail({
      ...defaultFormState,
      value: event.currentTarget.value,
      ...validateEmail(event.currentTarget.value),
    });
  };

  const isBtnDisabled =
    familyName.value.length === 0 ||
    familyName.isError ||
    givenName.value.length === 0 ||
    givenName.isError ||
    email.value.length === 0 ||
    email.isError;

  return (
    <Styled.DivGridContainer>
      <Styled.DivTextWrapper>
        <div>
          <Styled.ParRedText>イベント割対象商品</Styled.ParRedText>
          <Styled.SpanWhiteText>です。</Styled.SpanWhiteText>
        </div>

        <Styled.ParSubText>
          割引価格で購入するには下記の必要事項を入力と参加規約への同意が必要になります。
        </Styled.ParSubText>
      </Styled.DivTextWrapper>

      <Styled.DivInputFlex>
        <Styled.DivGridInputNameWrapper>
          <Styled.Label htmlFor="family-name">
            姓<Styled.SpanMandatory>*</Styled.SpanMandatory>
          </Styled.Label>

          <Input
            type="text"
            placeholder="DMM"
            name="family-name"
            value={familyName.value}
            maxCountLength={32}
            inputProps={{ id: "family-name", autoFocus: true, enterKeyHint: "next" }}
            isClearButton={false}
            isError={familyName.isError}
            errorText={familyName.errorText}
            onChange={handleFamilyNameChange}
          />
        </Styled.DivGridInputNameWrapper>

        <Styled.DivMarginRight />

        <Styled.DivGridInputNameWrapper>
          <Styled.Label htmlFor="given-name">
            名<Styled.SpanMandatory>*</Styled.SpanMandatory>
          </Styled.Label>

          <Input
            type="text"
            placeholder="太郎"
            name="given-name"
            value={givenName.value}
            maxCountLength={32}
            inputProps={{ id: "given-name", enterKeyHint: "next" }}
            isClearButton={false}
            isError={givenName.isError}
            errorText={givenName.errorText}
            onChange={handleGivenNameChange}
          />
        </Styled.DivGridInputNameWrapper>
      </Styled.DivInputFlex>

      <Styled.DivGridInputWrapper>
        <Styled.Label htmlFor="email-address">
          メールアドレス<Styled.SpanMandatory>*</Styled.SpanMandatory>
        </Styled.Label>

        <Input
          type="email"
          placeholder="dmmtaro@xxx.com"
          name="email"
          value={email.value}
          maxCountLength={64}
          inputProps={{ id: "email-address", inputMode: "email", enterKeyHint: "done" }}
          isClearButton={false}
          isError={email.isError}
          errorText={email.errorText}
          onChange={handleEmailChange}
        />
      </Styled.DivGridInputWrapper>

      <AgreementContent />

      <Styled.DivTextLinkCenter>
        <Styled.TextLink
          href="https://terms.dmm.com/privacy/"
          color="BLUE900"
          target="_blank"
          rel="noopener noreferrer"
        >
          個人情報保護に関して
        </Styled.TextLink>
      </Styled.DivTextLinkCenter>

      <Styled.DivBottomButtonWrapper>
        <Styled.Button
          name="go-to-the-cashier-button"
          mainText="レジへ進む"
          bgColor="YELLOW900"
          color="GRAY50"
          disabled={isBtnDisabled}
          {...handleClickByDevice(postPurchaseWakuwakuPfCashier)}
        />
      </Styled.DivBottomButtonWrapper>
    </Styled.DivGridContainer>
  );
};
